import React from 'react';
import { styled, Theme } from '@mui/material/styles';
import TrimbleMaps from '@trimblemaps/trimblemaps-js';
import { bbox } from '@turf/turf';

import { NetworkArea } from '../../interfaces/services/areas';
import { MapLayers, MapSources } from '../../utils/maps/enums';
import {
    fitMapToBounds,
    handleLineStringPopupMouseEnter,
    handlePopupMouseEnter,
    handlePopupMouseLeave,
    toggleLayerVisibility,
    trimbleMapsPopup,
} from '../../utils/maps/mapUtils';
import { addBalanceAreasCenterLayer } from '../../utils/maps/layers/balanceAreas';
import { addDensityAreasCenterLayer, addDensityAreasHeatmapLayer } from '../../utils/maps/layers/densityAreas';
import {
    addEmptyMilesAreasCenterLayer,
    addEmptyMilesAreasLaneCenterLayer,
    addEmptyMilesAreasLaneLineLayer,
} from '../../utils/maps/layers/emptyMilesAreas';
import { BalanceAreasMapSourceProperties, addBalanceAreasSource } from '../../utils/maps/sources/balanceAreas';
import { DensityAreasMapSourceProperties, addDensityAreasSource } from '../../utils/maps/sources/densityAreas';
import { EmptyMilesAreasMapSourceProperties, addEmptyMilesAreasSource } from '../../utils/maps/sources/emptyMilesAreas';
import MapSpinner from '../Loaders/MapSpinner';

import '../../assets/CSS/trimbleMaps.css';

const classesPrefix = 'areaHealthMap';

const classes = {
    map: `${classesPrefix}-map`,
};

const StyledDiv = styled('div')(() => {
    return {
        [`&.${classes.map}`]: {
            height: 'calc(100% - 48px)',
            minHeight: '300px',
        },
    };
});

interface AreaHealthMapProps {
    /** Indicator to show loading spinner while balance data is fetching. */
    isFetchingBalanceData: boolean;
    /** Indicator to show balance layers on map */
    showBalance: boolean;
    /** Balance areas to add to the map */
    balanceAreas: NetworkArea[];
    /** Indicator to show loading spinner while density data is fetching. */
    isFetchingDensityData: boolean;
    /** Indicator to show density layers on map */
    showDensity: boolean;
    /** Density areas to add to the map */
    densityAreas: NetworkArea[];
    /** Indicator to show loading spinner while empty miles data is fetching. */
    isFetchingEmptyMilesData: boolean;
    /** Indicator to show empty miles layers on map */
    showEmptyMiles: boolean;
    /** Ampty Miles areas to add to the map */
    emptyMilesAreas: NetworkArea[];
    /** MUI theme for styling the map layers */
    theme: Theme;
    /** Indicator to trigger a map resize function. */
    resize?: boolean;
}

interface AreaHealthMapState {
    mapStyleLoaded: boolean;
}

let map: TrimbleMaps.Map | null = null;
const mapContainer = React.createRef<HTMLDivElement>();

class AreaHealthMap extends React.Component<AreaHealthMapProps, AreaHealthMapState> {

    constructor(props: AreaHealthMapProps) {
        super(props);

        TrimbleMaps.APIKey = '173580F5C6F48F44B5006F78909958C1';

        this.state = {
            mapStyleLoaded: false,
        };
    }

    componentDidMount(): void {
        const latitude = 39.828347;
        const longitude = -98.579488;

        if (!mapContainer.current) { return; }

        map = new TrimbleMaps.Map({
            container: mapContainer.current, // Ties to an HTML DOM element
            center: [longitude, latitude], // Sets initial center of map
            style: TrimbleMaps.Common.Style.DATADARK, // Sets initial map style
            minZoom: 1.55, // To stop the zoom from showing multiple worlds
            zoom: 3, // Sets initial zoom level
        }).addControl(
            // Adds the zoom in/out and compass buttons
            new TrimbleMaps.NavigationControl()
        ).addControl(
            new TrimbleMaps.FullscreenControl()
        );

        map.setRegion(TrimbleMaps.Common.Region.WW);

        // README: The event `style.load` is no longer a public event of mapbox. We will need to use some combination of using `styledata` and `map.isStyleLoaded()` at some point.
        // However, these do not work the same and there are several open issues with mapbox that have yet to be resolved, such as this one:
        // https://github.com/mapbox/mapbox-gl-js/issues/9779
        // Once a map style is loaded, add any layers that were on back to the map
        map.on('style.load', (): void => {
            // The style.load event can fire after the component has been unmounted.
            if (map === null) {
                return;
            }

            this.removeBalanceAreasEventHandlers();
            this.addBalanceAreasToMap();

            this.removeDensityAreasEventHandlers();
            this.addDensityAreasToMap();

            this.removeEmptyMilesAreasEventHandlers();
            this.addEmptyMilesAreasToMap();

            // HACK: trigger a resize function to make map take up full width
            map.resize();
            this.setState({ mapStyleLoaded: true });
        });
    }

    componentDidUpdate(prevProps: AreaHealthMapProps): void {
        // if the map is null, hold any CDU updates
        if (map === null) {
            return;
        }

        if (this.state.mapStyleLoaded === false) {
            return;
        }

        // add balance areas to map if props have updated
        if (prevProps.balanceAreas !== this.props.balanceAreas) {
            this.removeBalanceAreasEventHandlers();
            this.addBalanceAreasToMap();
        }

        // toggle the balance area layers
        if (prevProps.showBalance !== this.props.showBalance) {
            toggleLayerVisibility(map, MapLayers.balanceAreasCenterLayer, this.props.showBalance);
        }

        // add balance areas to map if props have updated
        if (prevProps.densityAreas !== this.props.densityAreas) {
            this.removeDensityAreasEventHandlers();
            this.addDensityAreasToMap();
        }

        // toggle the density area layers
        if (prevProps.showDensity !== this.props.showDensity) {
            toggleLayerVisibility(map, MapLayers.densityAreasCenterLayer, this.props.showDensity);
            toggleLayerVisibility(map, MapLayers.densityAreasHeatmapLayer, this.props.showDensity);
        }

        // add empty miles areas to map if props have updated
        if (prevProps.emptyMilesAreas !== this.props.emptyMilesAreas) {
            this.removeEmptyMilesAreasEventHandlers();
            this.addEmptyMilesAreasToMap();
        }

        // toggle the empty miles layers
        if (prevProps.showEmptyMiles !== this.props.showEmptyMiles) {
            toggleLayerVisibility(map, MapLayers.emptyMilesAreasCenterLayer, this.props.showEmptyMiles);
            toggleLayerVisibility(map, MapLayers.emptyMilesAreasLaneCenterLayer, this.props.showEmptyMiles);
            toggleLayerVisibility(map, MapLayers.emptyMilesAreasLaneLineLayer, this.props.showEmptyMiles);
        }

        // if resize prop is passed and is true force map to resize.
        if (this.props.resize === true) {
            map.resize();
        }
    }

    componentWillUnmount(): void {
        this.removeBalanceAreasEventHandlers();
        this.removeDensityAreasEventHandlers();
        this.removeEmptyMilesAreasEventHandlers();
    }

    determineMapBounds = (): { bounds: TrimbleMaps.LngLatBounds; } => {
        const bounds = new TrimbleMaps.LngLatBounds();

        if (map) {
            const balanceSource = map.getSource(MapSources.balanceAreasSource);
            if (this.props.showBalance && balanceSource !== undefined) {
                // @ts-ignore
                const { _data: data } = balanceSource;
                if ((data as GeoJSON.FeatureCollection<GeoJSON.Point, BalanceAreasMapSourceProperties>).features.length > 0) {
                    const detailsBoundingBox = bbox(data);
                    const detailsBounds = new TrimbleMaps.LngLatBounds(detailsBoundingBox as [number, number, number, number]);
                    bounds.extend(detailsBounds);
                }
            }

            const densitySource = map.getSource(MapSources.densityAreasSource);
            if (this.props.showDensity && densitySource !== undefined) {
                // @ts-ignore
                const { _data: data } = densitySource;
                if ((data as GeoJSON.FeatureCollection<GeoJSON.Point, DensityAreasMapSourceProperties>).features.length > 0) {
                    const detailsBoundingBox = bbox(data);
                    const detailsBounds = new TrimbleMaps.LngLatBounds(detailsBoundingBox as [number, number, number, number]);
                    bounds.extend(detailsBounds);
                }
            }

            const emptyMilesSource = map.getSource(MapSources.emptyMilesAreasSource);
            if (this.props.showEmptyMiles && emptyMilesSource !== undefined) {
                // @ts-ignore
                const { _data: data } = emptyMilesSource;
                if ((data as GeoJSON.FeatureCollection<GeoJSON.Point | GeoJSON.LineString, EmptyMilesAreasMapSourceProperties>).features.length > 0) {
                    const detailsBoundingBox = bbox(data);
                    const detailsBounds = new TrimbleMaps.LngLatBounds(detailsBoundingBox as [number, number, number, number]);
                    bounds.extend(detailsBounds);
                }
            }
        }

        return { bounds };
    };

    addBalanceAreasToMap = (): void => {
        if (map) {
            // add map source
            addBalanceAreasSource(map, this.props.balanceAreas || [], this.props.theme);

            // add area centers
            addBalanceAreasCenterLayer({
                map,
                mapSource: MapSources.balanceAreasSource,
                isVisible: this.props.showBalance,
            });
            map.on('mouseenter', MapLayers.balanceAreasCenterLayer, (e): void => { handlePopupMouseEnter(map, e, trimbleMapsPopup); });
            map.on('mouseleave', MapLayers.balanceAreasCenterLayer, (): void => { handlePopupMouseLeave(map, trimbleMapsPopup); });

            const mapBounds = this.determineMapBounds();
            if (Object.keys(mapBounds.bounds).length > 0) {
                fitMapToBounds({
                    map,
                    bounds: mapBounds.bounds,
                    zoomMap: true,
                    maxZoom: 12,
                });
            }
        }
    };

    addDensityAreasToMap = (): void => {
        if (map) {
            // add map source
            addDensityAreasSource(map, this.props.densityAreas || [], this.props.theme);

            const densityAmounts = this.props.densityAreas.map((area) => {
                return area.average_daily_density_amount + 15;
            });

            const minDensityAmount = densityAmounts.length > 0 ? Math.min(...densityAmounts) - 3 : 0;
            const maxDensityAmount = densityAmounts.length > 0 ? Math.max(...densityAmounts) : 1;

            // add area heatmap
            addDensityAreasHeatmapLayer({
                map,
                mapSource: MapSources.densityAreasSource,
                theme: this.props.theme,
                minDensityAmount,
                maxDensityAmount,
                isVisible: this.props.showDensity,
            });

            // add area centers
            addDensityAreasCenterLayer({
                map,
                mapSource: MapSources.densityAreasSource,
                minDensityAmount,
                maxDensityAmount,
                isVisible: this.props.showDensity,
            });
            map.on('mouseenter', MapLayers.densityAreasCenterLayer, (e): void => { handlePopupMouseEnter(map, e, trimbleMapsPopup); });
            map.on('mouseleave', MapLayers.densityAreasCenterLayer, (): void => { handlePopupMouseLeave(map, trimbleMapsPopup); });

            const mapBounds = this.determineMapBounds();
            if (Object.keys(mapBounds.bounds).length > 0) {
                fitMapToBounds({
                    map,
                    bounds: mapBounds.bounds,
                    zoomMap: true,
                    maxZoom: 12,
                });
            }
        }
    };

    addEmptyMilesAreasToMap = (): void => {
        if (map) {
            // add map source
            addEmptyMilesAreasSource(map, this.props.emptyMilesAreas || [], this.props.theme);

            // add area lane lines
            addEmptyMilesAreasLaneLineLayer({
                map,
                mapSource: MapSources.emptyMilesAreasSource,
                isVisible: this.props.showEmptyMiles,
            });
            map.on('mouseenter', MapLayers.emptyMilesAreasLaneLineLayer, (e: any): void => {
                handleLineStringPopupMouseEnter(map, MapLayers.emptyMilesAreasLaneLineLayer, e, trimbleMapsPopup);
            });
            map.on('mouseleave', MapLayers.emptyMilesAreasLaneLineLayer, (): void => { handlePopupMouseLeave(map, trimbleMapsPopup); });

            // add area lane centers
            addEmptyMilesAreasLaneCenterLayer({
                map,
                mapSource: MapSources.emptyMilesAreasSource,
                isVisible: this.props.showEmptyMiles,
            });

            // add area centers
            addEmptyMilesAreasCenterLayer({
                map,
                mapSource: MapSources.emptyMilesAreasSource,
                isVisible: this.props.showEmptyMiles,
            });
            map.on('mouseenter', MapLayers.emptyMilesAreasCenterLayer, (e): void => { handlePopupMouseEnter(map, e, trimbleMapsPopup); });
            map.on('mouseleave', MapLayers.emptyMilesAreasCenterLayer, (): void => { handlePopupMouseLeave(map, trimbleMapsPopup); });

            const mapBounds = this.determineMapBounds();
            if (Object.keys(mapBounds.bounds).length > 0) {
                fitMapToBounds({
                    map,
                    bounds: mapBounds.bounds,
                    zoomMap: true,
                    maxZoom: 12,
                });
            }
        }
    };

    removeBalanceAreasEventHandlers = (): void => {
        if (map) {
            if (map.getLayer(MapLayers.balanceAreasCenterLayer) !== undefined) {
                map.off('mouseenter', MapLayers.balanceAreasCenterLayer, (e): void => { handlePopupMouseEnter(map, e, trimbleMapsPopup); });
                map.off('mouseleave', MapLayers.balanceAreasCenterLayer, (): void => { handlePopupMouseLeave(map, trimbleMapsPopup); });
            }
        }
    };

    removeDensityAreasEventHandlers = (): void => {
        if (map) {
            if (map.getLayer(MapLayers.densityAreasCenterLayer) !== undefined) {
                map.off('mouseenter', MapLayers.densityAreasCenterLayer, (e): void => { handlePopupMouseEnter(map, e, trimbleMapsPopup); });
                map.off('mouseleave', MapLayers.densityAreasCenterLayer, (): void => { handlePopupMouseLeave(map, trimbleMapsPopup); });
            }
        }
    };

    removeEmptyMilesAreasEventHandlers = (): void => {
        if (map) {
            if (map.getLayer(MapLayers.emptyMilesAreasCenterLayer) !== undefined) {
                map.off('mouseenter', MapLayers.emptyMilesAreasCenterLayer, (e): void => { handlePopupMouseEnter(map, e, trimbleMapsPopup); });
                map.off('mouseleave', MapLayers.emptyMilesAreasCenterLayer, (): void => { handlePopupMouseLeave(map, trimbleMapsPopup); });
            }

            if (map.getLayer(MapLayers.emptyMilesAreasLaneLineLayer) !== undefined) {
                map.off('mouseenter', MapLayers.emptyMilesAreasLaneLineLayer, (e): void => {
                    handleLineStringPopupMouseEnter(map, MapLayers.emptyMilesAreasLaneLineLayer, e, trimbleMapsPopup);
                });
                map.off('mouseleave', MapLayers.emptyMilesAreasLaneLineLayer, (): void => { handlePopupMouseLeave(map, trimbleMapsPopup); });
            }
        }
    };

    render(): React.JSX.Element {
        return (
            <StyledDiv
                id='trimble-map-container'
                ref={mapContainer}
                className={classes.map}
                data-qa='areaHealthMap-container'
            >
                {
                    (
                        (this.props.showBalance && this.props.isFetchingBalanceData) ||
                        (this.props.showDensity && this.props.isFetchingDensityData) ||
                        (this.props.showEmptyMiles && this.props.isFetchingEmptyMilesData)
                    ) &&
                    <MapSpinner />
                }
            </StyledDiv>
        );
    }

}

export default AreaHealthMap;
